// ============================================
// DEMOGRAPHICS DISPLAY SERVICE
// Display formatting, grouping, and privacy filtering.
// NO score computation — SQL is the single source of truth.
// ============================================

import type { Employee, CategoryStats, DemographicSegmentData } from '../types/results';
import type { AgeBucketKey, GenderKey, DemographicDimension } from '../constants/demographics';
import {
    AGE_BUCKETS,
    GENDERS,
    MIN_SEGMENT_SIZE,
    SEGMENT_COLORS,
} from '../constants/demographics';

/**
 * Display segment with label and color for UI rendering
 */
export interface DemographicDisplaySegment {
    key: string;
    labelHe: string;
    count: number;
    averages: CategoryStats;
    color: string;
    parentKey?: string;
}

/**
 * Compute age bucket from date of birth string.
 * Mirrors the SQL CASE logic for consistency.
 */
export function getAgeBucket(dateOfBirth: string | undefined): AgeBucketKey {
    if (!dateOfBirth) return 'unknown';

    const dob = new Date(dateOfBirth);
    if (isNaN(dob.getTime())) return 'unknown';

    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
    }

    if (age < 18) return 'unknown';
    if (age <= 24) return '18-24';
    if (age <= 34) return '25-34';
    if (age <= 44) return '35-44';
    if (age <= 54) return '45-54';
    return '55+';
}

/**
 * Normalize gender string to standard key.
 * Mirrors the SQL CASE logic.
 */
export function normalizeGender(gender: string | undefined): GenderKey | 'unknown' {
    if (!gender) return 'unknown';
    if (gender === 'male' || gender === 'female') return gender;
    if (gender === 'other' || gender === 'prefer_not_to_say') return 'other';
    return 'unknown';
}

/**
 * Get Hebrew label for a segment key.
 */
export function getSegmentLabel(segmentKey: string, dimension: DemographicDimension): string {
    if (dimension === 'gender') {
        const g = GENDERS[segmentKey as GenderKey];
        return g?.labelHe || segmentKey;
    }
    // Age: return bucket key as-is (numbers are universal)
    const bucket = AGE_BUCKETS.find(b => b.key === segmentKey);
    return bucket?.labelHe || segmentKey;
}

/**
 * Group employees by demographic dimension.
 * Used for Group View chart coloring (scatter, distribution, etc.).
 * Excludes 'unknown' segment.
 */
export function groupEmployeesByDemographic(
    employees: Employee[],
    dimension: DemographicDimension
): Map<string, Employee[]> {
    const groups = new Map<string, Employee[]>();

    for (const emp of employees) {
        const key = dimension === 'age'
            ? (emp.ageBucket || getAgeBucket(emp.dateOfBirth))
            : (emp.genderNormalized || normalizeGender(emp.gender));

        if (key === 'unknown') continue;

        if (!groups.has(key)) {
            groups.set(key, []);
        }
        groups.get(key)!.push(emp);
    }

    return groups;
}

/**
 * Filter out segments with fewer respondents than the privacy threshold.
 */
export function filterSegmentsByPrivacy(
    segments: DemographicSegmentData[],
    minSize?: number
): DemographicSegmentData[] {
    const threshold = minSize ?? MIN_SEGMENT_SIZE;
    return segments.filter(s => s.count >= threshold);
}

/**
 * Get color for a segment key.
 */
export function getSegmentColor(segmentKey: string): string {
    return SEGMENT_COLORS[segmentKey] || '#b9b1a2';
}

